import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStatus } from "../../state/status";
import { useSession } from "../../state/session";
import { ModelImportRefused } from "../../api/errors";
import * as annotationsApi from "../../api/annotations";
import * as imagesApi from "../../api/images";
import * as projectsApi from "../../api/projects";
import type { FolderList, ImageOut, Project } from "../../api/types";
import { ClassResolutionDialog } from "../../dialogs/ClassResolutionDialog";
import { ProjectSetupDialog } from "../../dialogs/ProjectSetupDialog";
import {
  STATUS_LABELS, bulkReviewPrompt, clickSelect, markEmptyPrompt, folderOptions, galleryKey, keepFolder, matchesSearch, selectionLine,
} from "../../lib/dashboard";
import { gate } from "../../lib/edition";
import { navigate } from "../../router";
import { PROJECTS_CHANGED, getCurrentProjectId, projectDataChanged, projectsChanged, setCurrentProjectId, useCurrentProjectId } from "../../state/currentProject";
import { useDialogs } from "../../ui/dialogs";
import { useShell } from "../../ui/shellActions";
import { Gallery, thumbs } from "./Gallery";
import { MenuButton } from "../../ui/MenuButton";
import { nextStep } from "../../lib/nextStep";
import { augmentAdvice } from "../../lib/augmentAdvice";
import { handOffSelection, takeGalleryView } from "../../state/handoff";
import { isAugmented } from "../../lib/augmentImages";
import { annotationStats, type AnnotationStats } from "../../api/ml";
import { Icon } from "../../ui/icons";
import { ZipError } from "../../lib/zip";
import { filesFromDrop, fromFiles, fromZip, isZip, planUpload, rootName, toFile, withPaths, type Plan, type Source } from "../../lib/upload";
import { MoveDialog, UploadDialog, type UploadChoice } from "../../dialogs/UploadDialog";

const IMAGE_ACCEPT = ".jpg,.jpeg,.png,.webp,.zip";

/** A licence gate's props, also disabled for another reason (busy, or a viewer) without losing its tooltip. */
const withEdit = (g: { disabled: boolean; title?: string }, alsoDisabled: boolean) => ({ ...g, disabled: g.disabled || alsoDisabled });

/** project_dashboard.py: projects on the left; images, filters and actions on the right. */
/** "1 image", "3 images". */
const plural = (count: number, one: string) => `${count} ${one}${count === 1 ? "" : "s"}`;

export function Dashboard() {
  const { canEdit, has, isAdmin } = useSession();
  const { say } = useStatus();
  const dlg = useDialogs();
  const [projects, setProjects] = useState<Project[]>([]);
  const [current, setCurrent] = useState<Project | null>(null);
  const [images, setImages] = useState<ImageOut[]>([]);
  const [folders, setFolders] = useState<FolderList | null>(null);
  const [folder, setFolder] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  // Augmented copies: all images (null), originals only, or copies only.
  const [augFilter, setAugFilter] = useState<"only" | "exclude" | null>(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const anchor = useRef<number | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [panel, setPanel] = useState<null | "setup">(null);
  const [resolve, setResolve] = useState<null | { refusal: ModelImportRefused; file: File; device: string; folder: string }>(null);
  const fileInputs = { images: useRef<HTMLInputElement>(null), folder: useRef<HTMLInputElement>(null), captures: useRef<HTMLInputElement>(null), coco: useRef<HTMLInputElement>(null) };

  const error = useCallback((title: string, e: unknown) => dlg.alert({ title, text: (e as Error).message }), [dlg]);

  // ── Projects ───────────────────────────────────────────────────────────────
  // Projects made elsewhere (the switcher's New project…) join the list.
  useEffect(() => {
    const load = () => {
      projectsApi.listProjects().then((ps) => {
        setProjects(ps);
        setCurrent((cur) => (cur ? ps.find((p) => p.id === cur.id) ?? cur : cur));     // a new name or setup
      }).catch(() => {});
    };
    window.addEventListener(PROJECTS_CHANGED, load);
    return () => window.removeEventListener(PROJECTS_CHANGED, load);
  }, []);
  useEffect(() => {
    projectsApi.listProjects().then((ps) => {
      setProjects(ps);
      const back = ps.find((p) => p.id === getCurrentProjectId());     // coming back from another page: the same project is selected
      if (back) {
        setCurrent(back);
        const v = takeGalleryView(back.id);     // Augment ▸ Show these images, Auto-annotate ▸ Review now
        if (v?.folder !== undefined) setFolder(v.folder);
        if (v?.status !== undefined) setStatus(v.status);
      }
    }).catch((e) => error("Could not load the projects", e));
  }, [error]);
  useEffect(() => { if (current) setCurrentProjectId(current.id); }, [current]);   // not on the first, empty render: that would forget the selection

  // The top bar's project switcher sets the project from outside the page.
  const wanted = useCurrentProjectId();
  useEffect(() => {
    if (wanted === null) { if (current) { setCurrent(null); setImages([]); setFolders(null); setSelected(new Set()); } return; }
    if (wanted !== current?.id) { const p = projects.find((x) => x.id === wanted); if (p) select(p); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, projects]);

  const select = (p: Project) => {
    if (current?.id === p.id) return;
    setCurrentProjectId(p.id);      // at once, so the switcher effect above never sees the previous project as wanted
    setCurrent(p); setFolder(null); setStatus(null); setAugFilter(null); setSearch(""); setSelected(new Set()); anchor.current = null; setImages([]); setFolders(null);
  };

  const newProject = async () => {
    const name = (await dlg.prompt({ title: "New project", label: "Project name" }))?.trim();
    if (!name) return;
    try {
      const p = await projectsApi.createProject(name); setProjects((ps) => [...ps, p]); select(p); projectsChanged();
      if (isAdmin) setPanel("setup");      // an admin answers the setup questions once, right away
    }
    catch (e) { await error("Could not create the project", e); }
  };

  const renameProject = async () => {
    if (!current) { await dlg.alert({ title: "Rename project", text: "Select a project first." }); return; }
    const name = (await dlg.prompt({ title: "Rename project", label: "Project name", initial: current.name }))?.trim();
    if (!name || name === current.name) return;
    try {
      const updated = await projectsApi.updateProject(current.id, { name });
      setProjects((ps) => ps.map((p) => (p.id === updated.id ? updated : p)));
      setCurrent(updated);
      projectsChanged();
    } catch (e) { await error("Could not rename the project", e); }
  };

  const deleteProject = async () => {
    if (!current) return;
    if (!(await dlg.confirm({ title: "Delete project", danger: true, yes: "Delete", no: "Cancel", text: `Delete '${current.name}' with all its images, annotations and models? This cannot be undone.` }))) return;
    try {
      await projectsApi.deleteProject(current.id);
      setProjects((ps) => ps.filter((p) => p.id !== current.id));
      setCurrent(null); setCurrentProjectId(null); setImages([]); setFolders(null); setSelected(new Set());
      projectsChanged();
    } catch (e) { await error("Could not delete the project", e); }
  };

  // ── Images ─────────────────────────────────────────────────────────────────
  const loadImages = useCallback(async (p: Project, f: string | null, s: string | null, a: "only" | "exclude" | null = null) => {
    try { setImages(await imagesApi.listImages(p.id, { folder: f, status: s, augmented: a })); }
    catch (e) { await error("Could not load the images", e); }
  }, [error]);

  const refreshFolders = useCallback(async (p: Project) => {
    try {
      const data = await imagesApi.listFolders(p.id);
      setFolders(data);
      setFolder((cur) => keepFolder(cur, folderOptions(data)));
    } catch { /* the folder list is a convenience; the gallery still works without it */ }
  }, []);

  useEffect(() => { if (current) void refreshFolders(current); }, [current, refreshFolders]);
  useEffect(() => { if (current) void loadImages(current, folder, status, augFilter); }, [current, folder, status, augFilter, loadImages]);

  const reload = async () => { if (current) { await refreshFolders(current); await loadImages(current, folder, status, augFilter); projectDataChanged(); } };
  const shell = useShell();
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => shell.register({ "page.after": () => reloadRef.current() }), [shell]);   // Tools ▸ Clear Unreviewed… refreshes the gallery

  // The project's counts (tiles, chips, the next step), read again whenever the gallery reloads.
  const [stats, setStats] = useState<AnnotationStats | null>(null);
  useEffect(() => {
    if (!current) { setStats(null); return; }
    let alive = true;
    annotationStats(current.id).then((x) => alive && setStats(x)).catch(() => alive && setStats(null));
    return () => { alive = false; };
  }, [current, images]);
  // Each project's counts for the overview cards; a card without them shows its creation date.
  const [overview, setOverview] = useState<Record<number, AnnotationStats>>({});
  useEffect(() => {
    if (current || !projects.length) return;
    let alive = true;
    void Promise.all(projects.map((p) => annotationStats(p.id).then((x) => [p.id, x] as const).catch(() => null)))
      .then((rows) => { if (alive) setOverview(Object.fromEntries(rows.filter((r) => r !== null))); });
    return () => { alive = false; };
  }, [current, projects]);

  const visible = useMemo(() => images.filter((i) => matchesSearch(i, search)), [images, search]);
  const order = useMemo(() => visible.map((i) => i.id), [visible]);

  // Ctrl+A / ⌘A selects every image shown (filters and search apply), Esc clears: lib/dashboard.ts galleryKey.
  const selectAll = useCallback(() => { setSelected(new Set(order)); anchor.current = order[0] ?? null; }, [order]);
  const clearSelection = useCallback(() => { setSelected(new Set()); anchor.current = null; }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const what = galleryKey(e, {
        typing: !!t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)),
        dialogOpen: !!document.querySelector('[role="dialog"]'),
      });
      if (!what || !current) return;
      if (what === "clear" && selected.size === 0) return;          // leave Esc alone when there is nothing to clear
      e.preventDefault();
      if (what === "select_all") selectAll(); else clearSelection();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, selected, selectAll, clearSelection]);

  /** The card's checkbox: add or remove one image, as Ctrl+click does. */
  const onToggle = (id: number) => {
    const r = clickSelect(selected, order, anchor.current, id, { ctrl: true, shift: false });
    anchor.current = r.anchor; setSelected(r.selected);
  };

  const onSelect = (id: number, e: React.MouseEvent) => {
    const r = clickSelect(selected, order, anchor.current, id, { ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey });
    anchor.current = r.anchor; setSelected(r.selected);
  };

  const requireProject = async (): Promise<Project | null> => {
    if (current) return current;
    await dlg.alert({ title: "No project", text: "Select a project first." });
    return null;
  };

  const withProgress = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    try { await fn(); } finally { setBusy(null); setProgress(null); }
  };

  // ── Uploads ────────────────────────────────────────────────────────────────
  // One way in (requested 2026-10-22: "make it simple"): pictures, a folder or zips, from Upload or a drop on the
  // gallery, become one plan (lib/upload.ts); one dialog asks which folder and what to do with names the project
  // already has; everything goes into that one folder. A detector's captures zip can bring its detections along.
  const [asking, setAsking] = useState<null | { plan: Plan; taken: Set<string>; resolve: (c: UploadChoice | null) => void }>(null);
  const [moving, setMoving] = useState<null | { count: number; resolve: (folder: string | null) => void }>(null);
  const folderNames = (folders?.folders ?? []).map((f) => f.folder);

  /** After an upload or a move: that folder in the gallery, whatever the filters were, so what changed is on screen. */
  const showFolder = (f: string) => { setFolder(f || null); setStatus(null); setAugFilter(null); setSearch(""); };

  const uploadAll = async (picked: { file: File; path: string }[]) => {
    const p = await requireProject();
    if (!p || !picked.length) return;
    const sources: Source[] = fromFiles(picked);
    for (const { file } of picked.filter((x) => isZip(x.file.name))) {
      try { sources.push(await fromZip(file)); } catch (e) { await error(`Could not open ${file.name}`, e); return; }
    }
    const plan = planUpload(sources);
    if (!plan.pictures.length) {
      const left = plan.skippedUndisplayable ? `\n\n${plural(plan.skippedUndisplayable, "TIFF/BMP file")} left out: the web app can only show JPEG, PNG and WebP.` : "";
      await dlg.alert({ title: "No pictures", text: `No JPEG, PNG or WebP pictures in ${plan.from || "what was chosen"}.${left}` });
      return;
    }
    // A name the project already has is not added (the server keeps the image it has), so the dialog says which.
    let taken = new Set<string>();
    try { taken = new Set((await imagesApi.listImages(p.id)).map((i) => i.filename)); } catch { /* then the server's own check is all there is */ }
    const choice = await new Promise<UploadChoice | null>((resolve) => setAsking({ plan, taken, resolve }));
    setAsking(null);
    if (!choice) return;
    if (choice.detections && plan.bundle) { await runCtrlx(p, plan.bundle, rootName(plan.bundle.name), choice.folder); return; }
    const n = choice.pictures.length;
    await withProgress("upload", async () => {
      let done = 0;
      try {
        setProgress({ done, total: n });
        // A batch at a time is read (unpacked, for a zip), sent and let go, so a big zip never sits in memory whole.
        for (const batch of imagesApi.batches(choice.pictures)) {
          const files = await Promise.all(batch.map(toFile));
          await imagesApi.uploadImages(p.id, files, { folder: choice.folder || null });
          done += batch.length;
          setProgress({ done, total: n });
        }
        await reload();
        showFolder(choice.folder);
        say(`${plural(n, "picture")} added${choice.folder ? ` to “${choice.folder}”` : ""}${choice.skipped ? `, ${choice.skipped} skipped (already here)` : ""}`);
      } catch (e) {
        await reload();
        const what = e instanceof ZipError ? "Could not unpack the zip" : "Could not upload the images";
        await error(what, new Error(`${(e as Error).message}${done ? `\n\n${done} of ${n} were uploaded before this.` : ""}`));
      }
    });
  };
  const onImages = (files: File[]) => uploadAll(withPaths(files));

  /** The selection bar's Move to folder…: the selected images on screen into one folder, or out of every folder. */
  const moveSelected = async () => {
    const p = await requireProject();
    const ids = visible.filter((i) => selected.has(i.id)).map((i) => i.id);
    if (!p || !ids.length) return;
    const target = await new Promise<string | null>((resolve) => setMoving({ count: ids.length, resolve }));
    setMoving(null);
    if (target === null) return;
    try {
      const r = await imagesApi.moveImages(p.id, ids, target);
      setSelected(new Set()); anchor.current = null;
      await reload();
      showFolder(target);
      say(target ? `${plural(r.images_moved, "image")} moved to “${target}”` : `${plural(r.images_moved, "image")} taken out of their folders`);
    } catch (e) { await error("Could not move the images", e); }
  };

  const onCaptures = async (files: File[]) => {
    const p = await requireProject();
    if (!p || !files.length) return;
    await withProgress("captures", async () => {
      try { await imagesApi.uploadCaptures(p.id, files); await reload(); } catch (e) { await error("Could not upload the images", e); }
    });
  };

  /** A detector's captures zip with its detections (POST …/import-captures), into the folder the Upload dialog named. */
  const runCtrlx = async (p: Project, file: File, device: string, folderName: string, create = false, mapping?: Record<string, string>) => {
    await withProgress("ctrlx", async () => {
      try {
        const r = await imagesApi.importCaptureBundle(p.id, file, device, create, mapping, folderName);
        const lines = [`${plural(r.images_created, "image")} imported from ${r.device}, with ${plural(r.annotations_created, "detection")} to review.`];
        if (r.images_already_imported) lines.push(`${r.images_already_imported} were already in the project and were left as they are.`);
        if (r.warning) lines.push(r.warning);
        const skipped = r.skipped ?? [];
        if (skipped.length) {
          const shown = skipped.slice(0, 10).map((s) => `  • ${s.file}: ${s.reason}`);
          lines.push([`${skipped.length} item(s) skipped:`, ...shown, ...(skipped.length > 10 ? [`  … and ${skipped.length - 10} more`] : [])].join("\n"));
        }
        lines.push("The detections are unreviewed: correct them and mark them reviewed before they are used for training.");
        await reload();
        showFolder(folderName);
        say(`${plural(r.images_created, "image")} imported from ${r.device}, ${plural(r.annotations_created, "detection")} to review`, undefined,
          { title: "ctrlX captures imported", text: lines.join("\n\n") });
      } catch (e) {
        if (e instanceof ModelImportRefused && e.status === 409 && e.missingClasses.length) setResolve({ refusal: e, file, device, folder: folderName });
        else await error("Could not import the captures", e);
      }
    });
  };

  const onCoco = async (files: File[]) => {
    const p = await requireProject();
    if (!p || !files[0]) return;
    const choice = await dlg.choose3({
      title: "Import annotations", yes: "Add to existing", no: "Replace",
      text: "Images that already have annotations: keep them and add these, or replace them with these? Only the images this file mentions are touched.",
    });
    if (choice === "cancel") return;
    try {
      const r = await imagesApi.importCoco(p.id, files[0], choice === "yes" ? "merge" : "replace");
      const lines = [`Imported ${plural(r.imported, "annotation")} onto ${plural(r.images_matched, "image")}.`];
      lines.push(r.marked_reviewed ? "They are marked reviewed, so they count towards training."
        : "They are not reviewed yet: the images are under Needs review. Check them and mark them reviewed to train on them.");
      if (r.classes_created?.length) lines.push(`New classes: ${r.classes_created.join(", ")}.`);
      if (r.images_not_found_count) lines.push(`${r.images_not_found_count} image(s) in the file are not in this project and were skipped: ${(r.images_not_found ?? []).join(", ")}`);
      if (r.skipped_invalid_boxes) lines.push(`${r.skipped_invalid_boxes} box(es) were unusable (zero size, or outside the image).`);
      await reload();
      say(lines[0].replace(/\.$/, ""), undefined, { title: "Annotations imported", text: lines.join("\n\n") });
    } catch (e) { await error("Could not import the annotations", e); }
  };

  const deleteSelected = async () => {
    const p = await requireProject();
    if (!p) return;
    const ids = images.filter((i) => selected.has(i.id));
    if (!ids.length) { await dlg.alert({ title: "No selection", text: "Select one or more images first." }); return; }
    if (!(await dlg.confirm({ title: "Delete images", danger: true, yes: "Delete", no: "Cancel", text: `Delete ${ids.length} selected image${ids.length === 1 ? "" : "s"} and their annotations? This cannot be undone.` }))) return;
    const errors: string[] = [];
    for (const img of ids) {
      try { await imagesApi.deleteImage(img.id); thumbs.clear(img.id); } catch (e) { errors.push(`${img.filename}: ${(e as Error).message}`); }
    }
    if (errors.length) await dlg.alert({ title: "Some images were not deleted", text: errors.join("\n") });
    setSelected(new Set());
    await reload();
  };

  /** Bulk review: the images on screen that are selected, counted first, then marked once the person agrees. */
  const reviewSelected = async () => {
    const p = await requireProject();
    if (!p) return;
    const ids = visible.filter((i) => selected.has(i.id)).map((i) => i.id);   // what is visible, so nothing hidden by a search is touched
    if (!ids.length) { await dlg.alert({ title: "No selection", text: "Select the images you have checked first." }); return; }
    try {
      const plan = bulkReviewPrompt(await annotationsApi.markImagesReviewed(p.id, { image_ids: ids, dry_run: true }));
      if (plan.kind === "nothing") { await dlg.alert({ title: "Mark reviewed", text: plan.text }); return; }
      if (!(await dlg.confirm({ title: "Mark reviewed", text: plan.text, yes: "Mark reviewed", no: "Cancel" }))) return;
      const done = await annotationsApi.markImagesReviewed(p.id, { image_ids: ids });
      say(`${done.annotations_marked} annotation${done.annotations_marked === 1 ? "" : "s"} on ${done.images_affected} image${done.images_affected === 1 ? "" : "s"} marked reviewed`);
      await reload();
    } catch (e) { await error("Could not mark them reviewed", e); }
  };

  /** The selected images hold none of the objects: counted first, then marked once the person agrees. */
  const markEmptySelected = async () => {
    const p = await requireProject();
    if (!p) return;
    const ids = visible.filter((i) => selected.has(i.id)).map((i) => i.id);   // what is visible, as for Mark reviewed
    if (!ids.length) { await dlg.alert({ title: "No selection", text: "Select the images that have none of your objects in them first." }); return; }
    try {
      const plan = markEmptyPrompt(await annotationsApi.markImagesEmpty(p.id, { image_ids: ids, dry_run: true }));
      if (plan.kind === "nothing") { await dlg.alert({ title: "Mark empty", text: plan.text }); return; }
      if (!(await dlg.confirm({ title: "Mark empty", text: plan.text, yes: "Mark empty", no: "Cancel" }))) return;
      const done = await annotationsApi.markImagesEmpty(p.id, { image_ids: ids });
      say(`${done.images_marked} image${done.images_marked === 1 ? "" : "s"} marked empty: training uses them as background`);
      await reload();
    } catch (e) { await error("Could not mark them empty", e); }
  };


  /** Like the desktop, check for a project before opening the file dialog. */
  const openPicker = async (ref: React.RefObject<HTMLInputElement>) => { if (await requireProject()) ref.current?.click(); };

  const pick = (ref: React.RefObject<HTMLInputElement>, handler: (f: File[]) => void) => ({
    ref, type: "file" as const, hidden: true,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => { const f = [...(e.target.files ?? [])]; e.target.value = ""; handler(f); },
  });

  const options = folderOptions(folders);
  const select2 = (v: string | null) => (v === null ? "" : v === "" ? "__none__" : v);

  // Drop pictures, a folder or zips from the desktop onto the gallery: the same upload as the button.
  const [dragging, setDragging] = useState(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragging(false);
    if (!current || !canEdit || busy) return;
    void filesFromDrop(e.dataTransfer).then(uploadAll);       // reads the drop before its first await, while it still can
  };
  const dragProps = canEdit && current ? {
    onDragOver: (e: React.DragEvent) => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); setDragging(true); } },
    onDragLeave: (e: React.DragEvent) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false); },
    onDrop,
  } : {};

  const next = stats ? nextStep(stats) : null;
  // Before training: suggest augmented copies when there are few images, or few of one class.
  const hint = stats && canEdit && has("augment_images") && (next?.action === "train" || next?.action === "auto" || next?.action === "deploy") ? augmentAdvice(stats) : null;
  const runNext = async () => {
    if (!current || !next) return;
    if (next.action === "upload") { fileInputs.images.current?.click(); return; }
    if (next.action === "review") { setStatus("auto_annotated"); setFolder(null); setSearch(""); return; }
    if (next.action === "annotate") {
      const first = images.find((i) => i.status === "unannotated") ?? images[0];
      navigate({ name: "annotate", projectId: current.id, imageId: first?.id });
      return;
    }
    navigate({ name: next.action === "auto" ? "auto" : "train", projectId: current.id });
  };
  const hasCopies = augFilter !== null || images.some(isAugmented);
  const chips: [string | null, string, number | undefined][] = stats ? [
    [null, "All", stats.total], ["annotated", STATUS_LABELS.annotated, stats.annotated], ["auto_annotated", STATUS_LABELS.auto_annotated, stats.auto_annotated],
    ["unannotated", STATUS_LABELS.unannotated, stats.unannotated],
    ...(stats.in_progress ? [["in_progress", STATUS_LABELS.in_progress, stats.in_progress] as [string, string, number]] : []),
    ...(stats.empty ? [["empty", STATUS_LABELS.empty, stats.empty] as [string, string, number]] : []),
  ] : [];

  const inputs = (
    <>
      <input {...pick(fileInputs.images, onImages)} multiple accept={IMAGE_ACCEPT} data-testid="input-images" />
      <input {...pick(fileInputs.folder, onImages)} multiple data-testid="input-folder" {...{ webkitdirectory: "" }} />
      <input {...pick(fileInputs.captures, onCaptures)} multiple accept=".npz,.ply" data-testid="input-captures" />
      <input {...pick(fileInputs.coco, onCoco)} accept=".json" data-testid="input-coco" />
    </>
  );

  // ── No project open: the organization's projects ─────────────────────────────────────────────
  if (!current) {
    return (
      <div className="dash-page" data-testid="dashboard">
        {inputs}
        <div className="page-head">
          <div>
            <h1 className="page-title" data-testid="project-name">Projects</h1>
            <div className="muted">{projects.length ? "Pick a project to open it." : "A project holds one set of images, its classes and the models trained on them."}</div>
          </div>
          <span className="spacer" />
          <button className="btn primary" disabled={!canEdit} onClick={newProject} data-testid="new-project">New project</button>
        </div>
        {projects.length === 0 ? (
          <div className="empty-state" data-testid="no-projects">
            <Icon name="layers" size={28} />
            <h2>Create your first project</h2>
            <p className="muted">Then upload images, annotate about 50 objects, and train a model that annotates the rest.</p>
            <button className="btn primary" disabled={!canEdit} onClick={newProject}>New project</button>
          </div>
        ) : (
          <div className="project-grid" role="list" data-testid="project-list">
            {projects.map((p) => {
              const st = overview[p.id];
              return (
                <button key={p.id} className="project-card" role="listitem" onClick={() => select(p)} data-testid={`project-${p.name}`}
                  onDoubleClick={() => { select(p); }}>
                  <span className="project-card-name">{p.name}</span>
                  <span className="project-card-facts">
                    {st ? <>
                      <span>{st.total} image{st.total === 1 ? "" : "s"}</span>
                      {st.auto_annotated > 0 && <span className="pill review"><i />{st.auto_annotated} to review</span>}
                      {st.latest_model_version && <span className="pill annotated"><i />Model v{st.latest_model_version}</span>}
                    </> : <span className="muted">Created {p.created_at.slice(0, 10)}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── A project open ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="dash-page" data-testid="dashboard">
      {inputs}
      <div className="page-head">
        <div style={{ minWidth: 0 }}>
          <h1 className="page-title" data-testid="project-name" onDoubleClick={() => canEdit && void renameProject()} title={canEdit ? "Double-click to rename" : undefined}>{current.name}</h1>
          <div className="muted" data-testid="project-facts">
            {stats ? `${stats.total} image${stats.total === 1 ? "" : "s"} · ${stats.reviewed_annotation_count} reviewed annotation${stats.reviewed_annotation_count === 1 ? "" : "s"}${stats.latest_model_version ? ` · model v${stats.latest_model_version}` : " · no model yet"}` : " "}
          </div>
        </div>
        <span className="spacer" />
        <MenuButton testId="import-menu" label={<><Icon name="import" />Import<Icon name="chevronDown" size={14} /></>} align="right">
          <button className="menu-item" disabled={!!busy || !canEdit} onClick={() => openPicker(fileInputs.coco)} data-testid="import-coco"
            title="Load annotations from a COCO JSON onto images already uploaded to this project (matched by filename).">
            <span className="menu-text"><span>Annotations (COCO JSON)</span><span className="menu-sub">Onto images already here, matched by filename</span></span><span className="key" /></button>
          <button className="menu-item" onClick={() => openPicker(fileInputs.captures)} data-testid="upload-captures"
            {...withEdit(gate(has("tof"), "Import ToF captures from a Helios2. Each one becomes an image you annotate as usual — its 850 nm intensity view — with the point cloud kept alongside, so an outline can be measured in millimetres without being told a scale or a distance."), !!busy || !canEdit)}>
            <span className="menu-text"><span>3D captures (point cloud){!has("tof") && <span className="pill pro" style={{ marginLeft: 8 }}>Pro</span>}</span>
              <span className="menu-sub">Helios2 ToF frames, measurable in millimetres</span></span><span className="key" /></button>
        </MenuButton>
        {/* Upload: pictures or zips from the button, a whole folder from its ▾ (a browser picks files or a folder, never both). */}
        <div className="split-btn">
          <button className="btn primary" disabled={!!busy || !canEdit} onClick={() => openPicker(fileInputs.images)} data-testid="upload-images"
            title="Pictures (JPEG, PNG, WebP) or zips of them. You can also drop pictures, a folder or a zip on the gallery."><Icon name="upload" />Upload</button>
          <MenuButton testId="upload-more" className="btn primary split-more" label={<Icon name="chevronDown" size={14} />} align="right" title="More ways to upload">
            <button className="menu-item" disabled={!!busy || !canEdit} onClick={() => openPicker(fileInputs.images)} data-testid="upload-files">
              <span className="menu-text"><span>Pictures or zips…</span><span className="menu-sub">JPEG, PNG, WebP, or .zip files of them</span></span><span className="key" /></button>
            <button className="menu-item" disabled={!!busy || !canEdit} onClick={() => openPicker(fileInputs.folder)} data-testid="upload-folder">
              <span className="menu-text"><span>A whole folder…</span><span className="menu-sub">Every picture in it, subfolders too</span></span><span className="key" /></button>
          </MenuButton>
        </div>
        <MenuButton testId="project-actions" className="btn ghost icon-btn" label={<Icon name="more" />} align="right" title="Project actions">
          <button className="menu-item" disabled={!canEdit} onClick={renameProject} data-testid="rename-project"><span>Rename project…</span><span className="key" /></button>
          {isAdmin && <button className="menu-item" onClick={() => setPanel("setup")} data-testid="project-setup-btn"><span>Project setup…</span><span className="key" /></button>}
          <div className="menu-sep" role="separator" />
          <button className="menu-item danger" disabled={!canEdit} onClick={deleteProject} data-testid="delete-project"><span>Delete project…</span><span className="key" /></button>
        </MenuButton>
      </div>

      <div className="tiles" data-testid="tiles">
        <div className="tile" data-testid="tile-images">
          <div className="tile-label">Images</div>
          <div className="tile-value">{stats?.total ?? "–"}</div>
          <div className="tile-detail">{stats ? `${stats.annotated} annotated · ${stats.unannotated} not annotated` : " "}</div>
        </div>
        <button className="tile tile-link" data-testid="tile-review" disabled={!stats?.auto_annotated}
          onClick={() => { setStatus("auto_annotated"); setFolder(null); setSearch(""); }} title="Show the images with annotations nobody has reviewed yet">
          <div className="tile-label">Needs review</div>
          <div className="tile-value">{stats?.auto_annotated ?? "–"}</div>
          <div className="tile-detail">{stats?.auto_annotated ? <span className="link">Show them</span> : "Nothing waiting"}</div>
        </button>
        <div className="tile" data-testid="tile-reviewed">
          <div className="tile-label">Reviewed annotations</div>
          <div className="tile-value">{stats?.reviewed_annotation_count ?? "–"}</div>
          <div className="tile-detail">{stats ? `on ${stats.reviewed_image_count} image${stats.reviewed_image_count === 1 ? "" : "s"}, used for training` : " "}</div>
        </div>
        <div className="tile tile-next" data-testid="tile-next">
          <div className="tile-label">Next step</div>
          {next ? <>
            <div className="tile-next-title" data-testid="next-title">{next.title}</div>
            {next.progress !== undefined && <div className="tile-bar"><i style={{ width: `${next.progress * 100}%` }} /></div>}
            <div className="tile-detail">{next.detail}</div>
            {hint && (
              <button className="btn flat tile-hint" onClick={() => navigate({ name: "augment", projectId: current.id, classId: hint.classId })} data-testid="next-augment"
                title={hint.text}>{hint.kind === "few" ? "Few images? Make augmented copies first." : `Few ${hint.className} examples? Make copies of those images.`}</button>
            )}
            <button className="btn primary tile-go" disabled={!canEdit && next.action !== "review"} onClick={() => void runNext()} data-testid="next-go">{next.button}</button>
          </> : <div className="tile-detail">Loading…</div>}
        </div>
      </div>

      <div className="filters">
        <div className="chips" role="radiogroup" aria-label="Status" data-testid="status-filter">
          {chips.map(([k, label, n]) => (
            <button key={label} className="chip" role="radio" aria-checked={status === k} onClick={() => setStatus(k)} data-testid={`chip-${k ?? "all"}`}>
              {label}<span className="chip-count">{n}</span>
            </button>
          ))}
        </div>
        {options.length > 1 && (
          <select className="select compact" value={select2(folder)} data-testid="folder-filter"
            onChange={(e) => setFolder(e.target.value === "" ? null : e.target.value === "__none__" ? "" : e.target.value)}>
            {options.map((o) => <option key={o.label} value={select2(o.value)}>{o.label}</option>)}
          </select>
        )}
        {hasCopies && (
          <select className="select compact" value={augFilter ?? ""} onChange={(e) => setAugFilter((e.target.value || null) as "only" | "exclude" | null)} data-testid="augmented-filter"
            title="Augmented copies are made on the Augment page (Pro).">
            <option value="">Originals and copies</option>
            <option value="exclude">Originals only</option>
            <option value="only">Augmented copies only</option>
          </select>
        )}
        <span className="spacer" />
        <input className="input search-input" placeholder="Search filenames…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search" />
        {selected.size === 0 && <span className="muted" data-testid="selection-count" />}
        <button className="btn flat" disabled={!order.length} onClick={selectAll} data-testid="select-all"
          title="Select every image shown (the folder, status and search filters apply). Shortcut: Ctrl+A (⌘A on a Mac).">Select all</button>
      </div>

      {busy && (
        <div className="progress" data-testid="progress" role="progressbar">
          {progress && <i style={{ width: `${Math.round((progress.done / Math.max(progress.total, 1)) * 100)}%` }} />}
          <span>{progress ? `${progress.done} / ${progress.total}` : "Working…"}</span>
        </div>
      )}

      <div className={`gallery-wrap${dragging ? " dragging" : ""}`} {...dragProps} data-testid="drop-zone">
        {stats && stats.total === 0 && !busy ? (
          <div className="empty-state" data-testid="empty-project">
            <Icon name="upload" size={28} />
            <h2>Drop pictures here to start</h2>
            <p className="muted">Pictures (JPEG, PNG, WebP), a folder of them or a zip. Or use Upload.</p>
            <ol className="steps-row">
              <li><b>1</b>Annotate about 50 objects by hand.</li>
              <li><b>2</b>Train a model on them.</li>
              <li><b>3</b>Let it annotate the rest, then review.</li>
            </ol>
            <button className="btn primary" disabled={!canEdit} onClick={() => openPicker(fileInputs.images)}><Icon name="upload" />Upload</button>
          </div>
        ) : visible.length === 0 && images.length > 0 ? (
          <div className="empty-state small"><p className="muted">No image matches “{search}”.</p></div>
        ) : (
          <Gallery images={visible} selected={selected} onSelect={onSelect} onToggle={onToggle}
            onOpen={(id) => navigate({ name: "annotate", projectId: current.id, imageId: id })} />
        )}
        {dragging && <div className="drop-hint"><Icon name="upload" size={22} />Drop to upload to {current.name}</div>}
      </div>

      {selected.size > 0 && (
        // Actions on the selected images appear only once there is a selection.
        <div className="selection-bar floating" data-testid="selection-bar">
          <b data-testid="selection-count">{selectionLine(selected, order)}</b>
          <button className="btn" onClick={reviewSelected} data-testid="review-selected"
            {...withEdit(gate(has("bulk_review"), "Mark every labelled, unreviewed annotation on the selected images as reviewed, so training uses them. Only for images you have checked; shapes and classes are not changed."), !canEdit)}>
            <Icon name="check" />Mark reviewed{!has("bulk_review") && <span className="pill pro">Pro</span>}</button>
          <button className="btn" onClick={() => { handOffSelection(current.id, [...selected]); navigate({ name: "augment", projectId: current.id }); }} data-testid="augment-selected"
            {...withEdit(gate(has("augment_images"), "Make augmented copies of the selected images."), !canEdit)}><Icon name="sparkles" />Augment</button>
          <button className="btn" disabled={!canEdit} onClick={moveSelected} data-testid="move-selected"
            title="Put the selected images into one folder, or take them out of their folders. Annotations and reviews stay as they are."><Icon name="folder" />Move to folder…</button>
          <button className="btn" disabled={!canEdit} onClick={markEmptySelected} data-testid="empty-selected"
            title="The selected images have none of your objects in them. Training uses them as background, which stops the model detecting things on an empty scene. Unreviewed annotations on them are deleted."><Icon name="eyeOff" />Mark empty</button>
          <button className="btn danger" disabled={!canEdit} onClick={deleteSelected} data-testid="delete-images"><Icon name="trash" />Delete</button>
          <button className="btn flat" onClick={clearSelection} data-testid="select-none" title="Clear the selection. Shortcut: Esc.">Clear</button>
        </div>
      )}

      {panel === "setup" && (
        <ProjectSetupDialog project={current} hasModels={!!stats?.has_trained_model} has={has} onClose={() => setPanel(null)}
          onSaved={(p) => { setProjects((ps) => ps.map((x) => (x.id === p.id ? p : x))); setCurrent(p); setPanel(null); projectsChanged(); }} />
      )}
      {asking && <UploadDialog plan={asking.plan} taken={asking.taken} folders={folderNames} onDone={asking.resolve} />}
      {moving && <MoveDialog count={moving.count} initial="" folders={folderNames} onDone={moving.resolve} />}
      {resolve && (
        <ClassResolutionDialog
          missing={resolve.refusal.missingClasses} projectClasses={resolve.refusal.projectClasses} suggestions={resolve.refusal.suggestions}
          onCancel={() => setResolve(null)}
          onImport={(mapping) => { const r = resolve; setResolve(null); void runCtrlx(current, r.file, r.device, r.folder, true, mapping); }}
        />
      )}
    </div>
  );
}
